import { isPowerOperator } from "@corca/core";
import { unitsJson } from "@corca/data";

import type { SemanticExpr } from "./types";
import type { CustomSympyUnit, InsertNode, NodeJson } from "@corca/data";

/** Extract display text from a unit entry's first plainText child. */
function getDisplayText(children: InsertNode[]): string {
  const first = children[0];
  if (first && "props" in first && first.props && "value" in first.props) {
    return (first.props as { value?: string }).value ?? "";
  }
  return "";
}

/**
 * Scalar-ratio units: dimensionless multipliers, not dimensional units. The value carries
 * a divisor (e.g. `5%` is `5/100`). They have no `sympyName`, so they resolve to no unit factor.
 */
export const SCALAR_RATIO_DIVISORS: Readonly<Record<string, number>> = {
  "%": 100,
  "‰": 1000,
  "‱": 10000,
};

/** Divisor for a scalar-ratio display string, or null if it is not a scalar ratio. */
export function getScalarRatioDivisor(displayText: string): number | null {
  return SCALAR_RATIO_DIVISORS[displayText] ?? null;
}

/** Display text → SymPy physics.units name. Derived from units.json `sympyName` field. */
export const UNIT_DISPLAY_TO_SYMPY: Readonly<Record<string, string>> = Object.fromEntries(
  unitsJson
    .filter((u): u is typeof u & { sympyName: string } => Boolean(u.sympyName))
    .map((u) => [getDisplayText(u.children), u.sympyName] as const)
    .filter(([display]) => display),
);

/**
 * Custom-Quantity units (engineering imperial: klf, ksi, ...) not in sympy.physics.units.
 * Each is referenced in emitted Python as `units.<name>` and defined in a preamble; order is
 * preserved so dependencies (lbf, kip) precede dependents (klf, ksi).
 */
export const CUSTOM_UNIT_DEFS: ReadonlyArray<{ display: string; def: CustomSympyUnit }> = unitsJson
  .filter((u): u is typeof u & { customSympy: CustomSympyUnit } => Boolean(u.customSympy))
  .map((u) => ({ display: getDisplayText(u.children), def: u.customSympy }))
  .filter(({ display }) => display);

const CUSTOM_UNIT_BY_DISPLAY: Readonly<Record<string, CustomSympyUnit>> = Object.fromEntries(
  CUSTOM_UNIT_DEFS.map(({ display, def }) => [display, def] as const),
);

/**
 * Reverse map: SymPy name → display string. Includes custom Quantity names, whose SymPy name is
 * the custom `def.name` (e.g. `klf`), so a round-tripped custom unit renders back to its display.
 */
export const SYMPY_UNIT_TO_DISPLAY: Readonly<Record<string, string>> = Object.fromEntries([
  ...Object.entries(UNIT_DISPLAY_TO_SYMPY).map(([display, sympy]) => [sympy, display] as const),
  ...CUSTOM_UNIT_DEFS.map(({ display, def }) => [def.name, display] as const),
]);

/** Resolve a display string to its `units.*` reference (native or custom Quantity), else null. */
export function resolveKnownUnit(text: string): string | null {
  const sympyName = UNIT_DISPLAY_TO_SYMPY[text];
  if (sympyName) return `units.${sympyName}`;
  const custom = CUSTOM_UNIT_BY_DISPLAY[text];
  if (custom) return `units.${custom.name}`;
  return null;
}

/** SI prefix display → SymPy prefix name (longest first to avoid partial matches). */
const SI_PREFIXES: ReadonlyArray<[string, string]> = [
  ["da", "deca"],
  ["Y", "yotta"],
  ["Z", "zetta"],
  ["E", "exa"],
  ["P", "peta"],
  ["T", "tera"],
  ["G", "giga"],
  ["M", "mega"],
  ["k", "kilo"],
  ["h", "hecto"],
  ["d", "deci"],
  ["c", "centi"],
  ["m", "milli"],
  ["μ", "micro"],
  ["n", "nano"],
  ["p", "pico"],
  ["f", "femto"],
  ["a", "atto"],
  ["z", "zepto"],
  ["y", "yocto"],
];

/** Extract integer exponent from an operator(power) node. */
function getExponentFromOperator(node: NodeJson): string | null {
  const exponentInline = node.children[0];
  if (!exponentInline || exponentInline.type !== "inline") return null;
  if (exponentInline.children.length !== 1) return null;
  const child = exponentInline.children[0];
  if (child.type !== "number") return null;
  const n = Number(child.props.value);
  return Number.isInteger(n) ? String(n) : null;
}

/* -------------------------------------------------------------------------- */
/* Structured (SemanticExpr) unit builder                                      */
/* Returns comparable unit/Pow factors so the input tree matches SymPy output. */
/* -------------------------------------------------------------------------- */

const unitLeaf = (name: string): SemanticExpr => ({ type: "unit", name });
const intNode = (value: string): SemanticExpr => ({ type: "number", kind: "integer", value });

/**
 * A unit or prefixed unit as a SemanticExpr: bare `unit`, or `Mul(prefix, unit)` for SI prefixes.
 * Custom Quantity units (klf, ksi, ...) resolve to a bare `unit` leaf named after their `def.name`;
 * `semanticToSympyCode` renders it as `units.<name>`, which `buildCustomUnitPreamble` then defines.
 */
function resolveBaseUnitSemantic(text: string): SemanticExpr | null {
  const sympyName = UNIT_DISPLAY_TO_SYMPY[text];
  if (sympyName) return unitLeaf(sympyName);

  const custom = CUSTOM_UNIT_BY_DISPLAY[text];
  if (custom) return unitLeaf(custom.name);

  for (const [prefix, sympyPrefix] of SI_PREFIXES) {
    if (text.startsWith(prefix) && text.length > prefix.length) {
      const remainder = text.slice(prefix.length);
      const remainderSympy = UNIT_DISPLAY_TO_SYMPY[remainder];
      if (remainderSympy) {
        return {
          type: "operator",
          name: "Mul",
          args: [unitLeaf(sympyPrefix), unitLeaf(remainderSympy)],
        };
      }
    }
  }
  return null;
}

/** `base` raised to `exp` (exp 1 → base itself, matching SymPy's Pow(unit, n) with a literal power). */
function unitFactor(base: SemanticExpr, exp: number): SemanticExpr {
  if (exp === 1) return base;
  return { type: "operator", name: "Pow", args: [base, intNode(String(exp))] };
}

/**
 * Returns the flat list of unit factors so the caller can build `Mul(number, ...factors)`.
 * Denominator units become negative-power factors (`Pow(unit, -n)`).
 * Returns null for a non-SI unit.
 */
export function unitInlineChildrenToSemantic(children: NodeJson[]): SemanticExpr[] | null {
  const factors: SemanticExpr[] = [];
  let inDenominator = false;

  for (let i = 0; i < children.length; i++) {
    const child = children[i];

    if (child.type === "plainText") {
      const text = (child.props as { value?: string }).value ?? "";
      // TODO: multiplication between units is (incorrectly) encoded as a "·" plainText separator,
      // unlike division which is an operatorSymbol. Until the node model uses an operatorSymbol here,
      // split on the mid-dot and treat each segment as an implicitly-multiplied unit factor.
      const segments = text.split("·").filter((s) => s.length > 0);
      if (segments.length === 0) continue;

      // A trailing power operator applies to the last unit in this plainText run.
      let exp = 1;
      const next = children[i + 1];
      if (isPowerOperator(next)) {
        const e = getExponentFromOperator(next);
        if (e !== null) {
          exp = Number(e);
          i += 1;
        }
      }

      for (let s = 0; s < segments.length; s++) {
        const base = resolveBaseUnitSemantic(segments[s]);
        if (!base) return null;
        const segExp = s === segments.length - 1 ? exp : 1;
        factors.push(unitFactor(base, inDenominator ? -segExp : segExp));
      }
    } else if (child.type === "operatorSymbol") {
      const sym = child.children.find((c) => c.type === "symbol");
      if (sym?.type === "symbol" && sym.props.key === "symbol-divide-by") {
        inDenominator = true;
      }
    }
  }

  return factors.length === 0 ? null : factors;
}

/* -------------------------------------------------------------------------- */
/* Custom Quantity preamble                                                    */
/* Operates on the emitted Python (post semanticToSympyCode), so it is         */
/* independent of the semantic-tree refactor: it scans for `units.<name>`.     */
/* -------------------------------------------------------------------------- */

/** Dimension tokens (e.g. "force", "length") referenced by a set of custom-unit definitions. */
function dimensionTokens(defs: readonly CustomSympyUnit[]): string[] {
  const tokens = new Set<string>();
  for (const def of defs) {
    for (const token of def.dimension.match(/[a-z_]+/g) ?? []) {
      tokens.add(token);
    }
  }
  return [...tokens];
}

/**
 * Python preamble lines defining every custom Quantity referenced (as `units.<name>`) in the
 * given emitted code, plus their transitive dependencies (klf's scale factor references kip,
 * kip's references lbf). Returns [] when none are used. Definitions follow `CUSTOM_UNIT_DEFS`
 * order so a dependency (units.lbf) is defined before a dependent (units.kip / units.klf).
 */
export function buildCustomUnitPreamble(...emittedCode: (string | undefined)[]): string[] {
  const haystack = emittedCode.filter(Boolean).join("\n");
  const needed = new Set<string>();

  // Seed from direct references, then close over each def's scaleFactor references. Iterate to a
  // fixed point over CUSTOM_UNIT_DEFS (dependency-ordered), reversed so a dependent seen first
  // still pulls in its dependency within one pass regardless of scan direction.
  const references = (text: string) =>
    CUSTOM_UNIT_DEFS.filter(({ def }) => new RegExp(`\\bunits\\.${def.name}\\b`).test(text));

  for (const { def } of references(haystack)) needed.add(def.name);
  let changed = true;
  while (changed) {
    changed = false;
    for (const { def } of CUSTOM_UNIT_DEFS) {
      if (!needed.has(def.name)) continue;
      for (const dep of references(def.scaleFactor)) {
        if (!needed.has(dep.def.name)) {
          needed.add(dep.def.name);
          changed = true;
        }
      }
    }
  }

  const used = CUSTOM_UNIT_DEFS.filter(({ def }) => needed.has(def.name));
  if (used.length === 0) return [];

  const dims = dimensionTokens(used.map(({ def }) => def));
  const lines = [`from sympy.physics.units import Quantity, ${dims.join(", ")}`];
  lines.push("from sympy.physics.units.systems.si import SI");
  for (const { def } of used) {
    lines.push(`units.${def.name} = Quantity('${def.name}')`);
    lines.push(`SI.set_quantity_dimension(units.${def.name}, ${def.dimension})`);
    lines.push(`SI.set_quantity_scale_factor(units.${def.name}, ${def.scaleFactor})`);
  }
  return lines;
}
